/**
 * The CSRF cookie is deliberately non-httpOnly (double-submit pattern) —
 * read it fresh from document.cookie on every request rather than caching
 * it in memory, so a page refresh doesn't lose it and force a re-login.
 */
function readCsrfCookie(): string | null {
  const match = document.cookie.match(/(?:^|; )bcp_csrf=([^;]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

export class ApiError extends Error {
  constructor(public status: number, public code: string, message?: string) {
    super(message ?? code);
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  isForm?: boolean;
}

/**
 * Thin fetch wrapper: always sends the session cookie, always attaches the
 * CSRF header for mutating requests (the token is only ever kept in memory
 * here — never read back out of the httpOnly session cookie, since it
 * can't be).
 */
export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? "GET";
  const headers: Record<string, string> = {};
  let body: BodyInit | undefined;

  if (options.body !== undefined) {
    if (options.isForm) {
      body = options.body as FormData;
    } else {
      headers["Content-Type"] = "application/json";
      body = JSON.stringify(options.body);
    }
  }

  if (method !== "GET" && method !== "HEAD") {
    const csrfToken = readCsrfCookie();
    if (csrfToken) headers["X-CSRF-Token"] = csrfToken;
  }

  const res = await fetch(`/api/v1${path}`, {
    method,
    headers,
    body,
    credentials: "include",
  });

  if (res.status === 204) {
    return undefined as T;
  }

  const contentType = res.headers.get("content-type") ?? "";
  const payload = contentType.includes("application/json") ? await res.json() : await res.text();

  if (!res.ok) {
    const message = typeof payload === "object" && payload && "message" in payload ? String((payload as { message?: unknown }).message) : undefined;
    const code = typeof payload === "object" && payload && "error" in payload ? String((payload as { error?: unknown }).error) : `HTTP_${res.status}`;
    throw new ApiError(res.status, code, message);
  }

  return payload as T;
}

export function apiDownloadUrl(path: string): string {
  return `/api/v1${path}`;
}
